import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import bodyDiagram from '../../assets/body-diagram.webp';

// ─── Sub-region definitions for detailed body areas ──────────────────────────

const SUB_REGIONS = {
  foot: [
    { id: 'heel_pad', label: 'Heel pad' },
    { id: 'arch', label: 'Arch (medial)' },
    { id: 'ball_of_foot', label: 'Ball of foot' },
    { id: 'dorsum', label: 'Top of foot (dorsum)' },
    { id: 'lateral_border', label: 'Outer foot (lateral border)' },
    { id: 'toes', label: 'Toes' },
  ],
  ankle: [
    { id: 'inner_ankle', label: 'Inner ankle (medial malleolus)' },
    { id: 'outer_ankle', label: 'Outer ankle (lateral malleolus)' },
    { id: 'front_ankle', label: 'Front of ankle' },
    { id: 'achilles', label: 'Achilles / back of ankle' },
  ],
  lower_leg: [
    { id: 'outer_shin', label: 'Outer shin (anterior compartment)' },
    { id: 'inner_shin', label: 'Inner shin (medial tibial — shin splint zone)' },
    { id: 'calf_upper', label: 'Calf upper (gastrocnemius)' },
    { id: 'calf_lower', label: 'Calf lower (soleus)' },
  ],
  knee: [
    { id: 'kneecap', label: 'Front / kneecap (patella)' },
    { id: 'outer_knee', label: 'Outer knee (IT band insertion)' },
    { id: 'inner_knee', label: 'Inner knee (medial)' },
    { id: 'back_knee', label: 'Back of knee (popliteal)' },
    { id: 'patellar_tendon', label: 'Patellar tendon (below kneecap)' },
  ],
  thigh: [
    { id: 'quad', label: 'Front upper (quad)' },
    { id: 'adductor', label: 'Inner thigh (adductor / groin)' },
    { id: 'it_band', label: 'Outer thigh (IT band)' },
    { id: 'hamstring', label: 'Back of thigh (hamstring)' },
  ],
  hip: [
    { id: 'hip_flexor', label: 'Hip flexor (front)' },
    { id: 'glute_med', label: 'Outer hip (glute med)' },
    { id: 'groin_origin', label: 'Groin / adductor origin' },
    { id: 'sit_bone', label: 'Sit bone (proximal hamstring)' },
    { id: 'si_joint', label: 'SI joint (back)' },
  ],
  lower_back: [
    { id: 'left_lumbar', label: 'Left lumbar' },
    { id: 'right_lumbar', label: 'Right lumbar' },
    { id: 'sacrum', label: 'Sacrum / tailbone' },
  ],
};

const REGION_LABELS = {
  foot: 'Foot', ankle: 'Ankle', lower_leg: 'Lower Leg', knee: 'Knee',
  thigh: 'Thigh', hip: 'Hip & Pelvis', lower_back: 'Lower Back',
};

// ─── Hotspot definitions mapped to the 250×250 body diagram ──────────────────
// Front body centered ~x=63, Back body centered ~x=187
// In front view: person's LEFT = viewer's right (x > center)
// In back view:  person's LEFT = viewer's left (x < center)

const BODY_HOTSPOTS = [
  // ── Front body (center x≈71) — upper (no sub-regions) ──
  { id: 'head',       label: 'Head',          cx: 71,  cy: 27,  rx: 13, ry: 13, side: null, region: null },
  { id: 'neck',       label: 'Neck',          cx: 71,  cy: 43,  rx: 7,  ry: 5,  side: null, region: null },
  { id: 'r_shoulder', label: 'R. Shoulder',   cx: 52,  cy: 56,  rx: 11, ry: 7,  side: 'right', region: null },
  { id: 'l_shoulder', label: 'L. Shoulder',   cx: 90,  cy: 56,  rx: 11, ry: 7,  side: 'left',  region: null },
  { id: 'chest',      label: 'Chest',         cx: 71,  cy: 72,  rx: 15, ry: 11, side: null, region: null },
  { id: 'r_arm',      label: 'R. Arm',        cx: 40,  cy: 92,  rx: 7,  ry: 18, side: 'right', region: null },
  { id: 'l_arm',      label: 'L. Arm',        cx: 102, cy: 92,  rx: 7,  ry: 18, side: 'left',  region: null },
  { id: 'abdomen',    label: 'Abdomen / Core', cx: 71, cy: 98,  rx: 14, ry: 10, side: null, region: null },

  // ── Front body — lower (with sub-regions) ──
  { id: 'r_hip',       label: 'R. Hip',        cx: 62,  cy: 118, rx: 10, ry: 8,  side: 'right', region: 'hip' },
  { id: 'l_hip',       label: 'L. Hip',        cx: 80,  cy: 118, rx: 10, ry: 8,  side: 'left',  region: 'hip' },
  { id: 'r_thigh',     label: 'R. Thigh',      cx: 61,  cy: 148, rx: 9,  ry: 20, side: 'right', region: 'thigh' },
  { id: 'l_thigh',     label: 'L. Thigh',      cx: 81,  cy: 148, rx: 9,  ry: 20, side: 'left',  region: 'thigh' },
  { id: 'r_knee',      label: 'R. Knee',       cx: 60,  cy: 175, rx: 8,  ry: 7,  side: 'right', region: 'knee' },
  { id: 'l_knee',      label: 'L. Knee',       cx: 82,  cy: 175, rx: 8,  ry: 7,  side: 'left',  region: 'knee' },
  { id: 'r_lower_leg', label: 'R. Shin',       cx: 59,  cy: 195, rx: 7,  ry: 14, side: 'right', region: 'lower_leg' },
  { id: 'l_lower_leg', label: 'L. Shin',       cx: 83,  cy: 195, rx: 7,  ry: 14, side: 'left',  region: 'lower_leg' },
  { id: 'r_ankle',     label: 'R. Ankle',      cx: 59,  cy: 215, rx: 7,  ry: 5,  side: 'right', region: 'ankle' },
  { id: 'l_ankle',     label: 'L. Ankle',      cx: 83,  cy: 215, rx: 7,  ry: 5,  side: 'left',  region: 'ankle' },
  { id: 'r_foot',      label: 'R. Foot',       cx: 58,  cy: 227, rx: 8,  ry: 8,  side: 'right', region: 'foot' },
  { id: 'l_foot',      label: 'L. Foot',       cx: 84,  cy: 227, rx: 8,  ry: 8,  side: 'left',  region: 'foot' },

  // ── Back body (center x≈177) — upper (no sub-regions) ──
  { id: 'head_b',       label: 'Head',          cx: 177, cy: 27,  rx: 13, ry: 13, side: null, region: null },
  { id: 'neck_b',       label: 'Neck',          cx: 177, cy: 43,  rx: 7,  ry: 5,  side: null, region: null },
  { id: 'l_shoulder_b', label: 'L. Shoulder',   cx: 158, cy: 56,  rx: 11, ry: 7,  side: 'left',  region: null },
  { id: 'r_shoulder_b', label: 'R. Shoulder',   cx: 196, cy: 56,  rx: 11, ry: 7,  side: 'right', region: null },
  { id: 'upper_back',   label: 'Upper Back',    cx: 177, cy: 72,  rx: 15, ry: 11, side: null, region: null },
  { id: 'l_arm_b',      label: 'L. Arm',        cx: 146, cy: 92,  rx: 7,  ry: 18, side: 'left',  region: null },
  { id: 'r_arm_b',      label: 'R. Arm',        cx: 208, cy: 92,  rx: 7,  ry: 18, side: 'right', region: null },

  // ── Back body — lower (with sub-regions) ──
  { id: 'lower_back',    label: 'Lower Back',   cx: 177, cy: 100, rx: 14, ry: 10, side: null,    region: 'lower_back' },
  { id: 'l_glute',       label: 'L. Hip/Glute', cx: 168, cy: 118, rx: 10, ry: 8,  side: 'left',  region: 'hip' },
  { id: 'r_glute',       label: 'R. Hip/Glute', cx: 186, cy: 118, rx: 10, ry: 8,  side: 'right', region: 'hip' },
  { id: 'l_hamstring',   label: 'L. Hamstring',  cx: 167, cy: 148, rx: 9,  ry: 20, side: 'left',  region: 'thigh' },
  { id: 'r_hamstring',   label: 'R. Hamstring',  cx: 187, cy: 148, rx: 9,  ry: 20, side: 'right', region: 'thigh' },
  { id: 'l_knee_b',      label: 'L. Knee (back)', cx: 166, cy: 175, rx: 8,  ry: 7, side: 'left',  region: 'knee' },
  { id: 'r_knee_b',      label: 'R. Knee (back)', cx: 188, cy: 175, rx: 8,  ry: 7, side: 'right', region: 'knee' },
  { id: 'l_calf',        label: 'L. Calf',       cx: 165, cy: 195, rx: 7,  ry: 14, side: 'left',  region: 'lower_leg' },
  { id: 'r_calf',        label: 'R. Calf',       cx: 189, cy: 195, rx: 7,  ry: 14, side: 'right', region: 'lower_leg' },
  { id: 'l_ankle_b',     label: 'L. Achilles',   cx: 165, cy: 215, rx: 7,  ry: 5,  side: 'left',  region: 'ankle' },
  { id: 'r_ankle_b',     label: 'R. Achilles',   cx: 189, cy: 215, rx: 7,  ry: 5,  side: 'right', region: 'ankle' },
  { id: 'l_foot_b',      label: 'L. Heel',       cx: 164, cy: 227, rx: 8,  ry: 8,  side: 'left',  region: 'foot' },
  { id: 'r_foot_b',      label: 'R. Heel',       cx: 190, cy: 227, rx: 8,  ry: 8,  side: 'right', region: 'foot' },
];

export const INJURY_CATEGORIES = [
  { id: 'muscle',  label: 'Muscle',  desc: 'Strain, pull, or tear' },
  { id: 'joint',   label: 'Joint',   desc: 'Ligament, cartilage, or joint pain' },
  { id: 'bone',    label: 'Bone',    desc: 'Stress fracture or bone pain' },
  { id: 'unsure',  label: 'Unsure',  desc: "I'm not sure what it is" },
];

export const ILLNESSES = [
  'COVID-19', 'Flu (Influenza)', 'Common Cold', 'Bronchitis',
  'Strep Throat', 'Sinus Infection', 'Stomach Virus / GI', 'Allergies',
  'Ear Infection', 'Mono (EBV)', 'Pneumonia', 'Asthma Flare-up',
  'Fatigue / Overtraining', 'Other',
];

// ─── Body Map with image background ─────────────────────────────────────────

function BodyMap({ selected, onSelect }) {
  const svgRef = React.useRef(null);
  const imgRef = React.useRef(null);

  React.useEffect(() => {
    const svgEl = svgRef.current;
    const imgEl = imgRef.current;
    if (!svgEl || !imgEl) return;
    const svgRect = svgEl.getBoundingClientRect();
    const imgRect = imgEl.getBoundingClientRect();
    console.log('=== BODY MAP DEBUG ===');
    console.log('SVG bounding box:', { x: svgRect.x, y: svgRect.y, width: svgRect.width, height: svgRect.height });
    console.log('IMG bounding box:', { x: imgRect.x, y: imgRect.y, width: imgRect.width, height: imgRect.height });
    console.log('ViewBox: 0 0 250 250');
    console.log('Scale factor:', { x: svgRect.width / 250, y: svgRect.height / 250 });
    console.log('--- Hotspot positions (viewBox units) ---');
    BODY_HOTSPOTS.forEach(p => {
      const screenX = svgRect.x + (p.cx / 250) * svgRect.width;
      const screenY = svgRect.y + (p.cy / 250) * svgRect.height;
      console.log(`${p.id.padEnd(18)} label="${p.label.padEnd(18)}" cx=${String(p.cx).padStart(3)} cy=${String(p.cy).padStart(3)} rx=${String(p.rx).padStart(2)} ry=${String(p.ry).padStart(2)}  → screen(${Math.round(screenX)}, ${Math.round(screenY)})`);
    });
    console.log('=== END DEBUG ===');
  }, []);

  return (
    <div className="hf-body-map-container">
      <div className="hf-body-map-labels">
        <span className="hf-body-map-view-label">FRONT</span>
        <span className="hf-body-map-view-label">BACK</span>
      </div>
      <div className="hf-body-map-wrap">
        <img ref={imgRef} src={bodyDiagram} alt="Body diagram" className="hf-body-map-img" draggable={false} />
        <svg ref={svgRef} viewBox="0 0 250 250" className="hf-body-map-svg">
          {BODY_HOTSPOTS.map(p => {
            const isSelected = selected?.id === p.id;
            return (
              <ellipse
                key={p.id}
                cx={p.cx}
                cy={p.cy}
                rx={p.rx}
                ry={p.ry}
                className={`body-hotspot${isSelected ? ' body-hotspot--selected' : ''}`}
                onClick={() => onSelect(isSelected ? null : p)}
              >
                <title>{p.label}</title>
              </ellipse>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function HealthFlagModal({ flagDate, onClose, onSaved, athleteId }) {
  const { profile } = useAuth();
  // Screens: type → body → subregion → category → details
  //          type → illness → details
  const [screen, setScreen] = useState('type');
  const [flagType, setFlagType] = useState(null);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [selectedSubRegion, setSelectedSubRegion] = useState(null);
  const [injuryCategory, setInjuryCategory] = useState(null);
  const [painScale, setPainScale] = useState(5);
  const [selectedIllness, setSelectedIllness] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  function selectType(type) {
    setFlagType(type);
    setScreen(type === 'injury' ? 'body' : 'illness');
  }

  function handleBodySelect(hotspot) {
    setSelectedHotspot(hotspot);
    setSelectedSubRegion(null);
  }

  function handleBodyNext() {
    if (!selectedHotspot) return;
    if (selectedHotspot.region && SUB_REGIONS[selectedHotspot.region]) {
      setScreen('subregion');
    } else {
      setScreen('category');
    }
  }

  // Build the display label for the selected area
  function getLocationLabel() {
    const parts = [];
    if (selectedHotspot?.side) {
      parts.push(selectedHotspot.side === 'left' ? 'Left' : 'Right');
    }
    if (selectedHotspot?.region) {
      parts.push(REGION_LABELS[selectedHotspot.region] ?? selectedHotspot.label);
    } else if (selectedHotspot) {
      parts.push(selectedHotspot.label);
    }
    if (selectedSubRegion) {
      parts.push('—');
      parts.push(selectedSubRegion.label);
    }
    return parts.join(' ');
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    const data = {
      athlete_id: athleteId ?? profile.id,
      flag_date: flagDate,
      type: flagType,
      notes: notes.trim() || null,
    };
    if (flagType === 'injury') {
      const label = getLocationLabel();
      data.body_part = label;
      data.body_part_id = selectedHotspot?.id ?? null;
      data.body_sub_part = selectedSubRegion?.label ?? null;
      data.injury_category = injuryCategory;
      data.pain_scale = painScale;
    } else {
      data.illness_type = selectedIllness;
    }
    const { error } = await supabase.from('health_flags').insert(data);
    setSaving(false);
    if (error) {
      setSaveError('Failed to send flag. Please try again.');
    } else {
      setSaved(true);
      onSaved?.();
    }
  }

  // ── Saved confirmation ──────────────────────────────────────────────────
  if (saved) {
    return (
      <div className="hf-overlay" onClick={onClose}>
        <div className="hf-modal" onClick={e => e.stopPropagation()}>
          <div className="tl-success">
            <div className="hf-success-icon">!</div>
            <div className="tl-success-heading">Your coach has been notified</div>
            <div className="tl-success-body">
              Take care of yourself first. Your coach will review this and adjust training as needed.
            </div>
            <button
              className="btn btn-primary"
              style={{ marginTop: 24, width: 'auto', padding: '10px 28px' }}
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hf-overlay" onClick={onClose}>
      <div className="hf-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="tl-modal-header">
          <div>
            <div className="tl-modal-title">Report a Health Issue</div>
            <div className="tl-modal-step-count">
              {screen === 'type'      && 'What happened?'}
              {screen === 'body'      && 'Where does it hurt?'}
              {screen === 'subregion' && `${selectedHotspot?.side === 'left' ? 'Left' : selectedHotspot?.side === 'right' ? 'Right' : ''} ${REGION_LABELS[selectedHotspot?.region] ?? ''} — Specific area`.trim()}
              {screen === 'category'  && 'What type of injury?'}
              {screen === 'illness'   && 'What illness?'}
              {screen === 'details'   && 'Tell us more'}
            </div>
          </div>
          <button className="day-modal-close" type="button" onClick={onClose}>×</button>
        </div>

        {/* ── Screen: type selection ──────────────────────────────────── */}
        {screen === 'type' && (
          <div className="hf-type-grid">
            <button className="hf-type-btn" onClick={() => selectType('injury')}>
              <span className="hf-type-icon">🦵</span>
              <span className="hf-type-label">Injury</span>
              <span className="hf-type-desc">Something hurts physically</span>
            </button>
            <button className="hf-type-btn" onClick={() => selectType('illness')}>
              <span className="hf-type-icon">🤒</span>
              <span className="hf-type-label">Illness</span>
              <span className="hf-type-desc">I'm feeling sick</span>
            </button>
          </div>
        )}

        {/* ── Screen: body map (front + back simultaneous) ───────────── */}
        {screen === 'body' && (
          <div className="hf-body-screen">
            <BodyMap selected={selectedHotspot} onSelect={handleBodySelect} />
            <div className={`hf-selected-label${!selectedHotspot ? ' hf-selected-label--hint' : ''}`}>
              {selectedHotspot ? <>Selected: <strong>{selectedHotspot.label}</strong></> : 'Tap the area that hurts'}
            </div>
            <div className="tl-modal-footer">
              <button className="tl-btn-back" type="button" onClick={() => { setScreen('type'); setSelectedHotspot(null); }}>← Back</button>
              <button
                className="btn btn-primary"
                style={{ width: 'auto', padding: '10px 24px' }}
                disabled={!selectedHotspot}
                onClick={handleBodyNext}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── Screen: sub-region detail picker ───────────────────────── */}
        {screen === 'subregion' && selectedHotspot?.region && (
          <div className="hf-subregion-screen">
            <div className="hf-subregion-grid">
              {SUB_REGIONS[selectedHotspot.region].map(sub => (
                <button
                  key={sub.id}
                  className={`hf-subregion-btn${selectedSubRegion?.id === sub.id ? ' hf-subregion-btn--selected' : ''}`}
                  onClick={() => setSelectedSubRegion(selectedSubRegion?.id === sub.id ? null : sub)}
                >
                  {sub.label}
                </button>
              ))}
            </div>
            <div className="tl-modal-footer">
              <button className="tl-btn-back" type="button" onClick={() => setScreen('body')}>← Back</button>
              <button
                className="btn btn-primary"
                style={{ width: 'auto', padding: '10px 24px' }}
                disabled={!selectedSubRegion}
                onClick={() => setScreen('category')}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── Screen: injury category ────────────────────────────────── */}
        {screen === 'category' && (
          <div className="hf-category-screen">
            <div className="hf-category-grid">
              {INJURY_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  className={`hf-category-btn${injuryCategory === cat.id ? ' hf-category-btn--selected' : ''}`}
                  onClick={() => setInjuryCategory(injuryCategory === cat.id ? null : cat.id)}
                >
                  <span className="hf-category-label">{cat.label}</span>
                  <span className="hf-category-desc">{cat.desc}</span>
                </button>
              ))}
            </div>
            <div className="tl-modal-footer">
              <button
                className="tl-btn-back"
                type="button"
                onClick={() => setScreen(selectedHotspot?.region ? 'subregion' : 'body')}
              >
                ← Back
              </button>
              <button
                className="btn btn-primary"
                style={{ width: 'auto', padding: '10px 24px' }}
                disabled={!injuryCategory}
                onClick={() => setScreen('details')}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── Screen: illness picker ─────────────────────────────────── */}
        {screen === 'illness' && (
          <div className="hf-illness-screen">
            <div className="illness-grid">
              {ILLNESSES.map(ill => (
                <button
                  key={ill}
                  className={`illness-btn${selectedIllness === ill ? ' illness-btn--selected' : ''}`}
                  onClick={() => setSelectedIllness(ill)}
                >
                  {ill}
                </button>
              ))}
            </div>
            <div className="tl-modal-footer">
              <button className="tl-btn-back" type="button" onClick={() => setScreen('type')}>← Back</button>
              <button
                className="btn btn-primary"
                style={{ width: 'auto', padding: '10px 24px' }}
                disabled={!selectedIllness}
                onClick={() => setScreen('details')}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── Screen: details (pain scale + notes) ───────────────────── */}
        {screen === 'details' && (
          <div className="hf-details-screen">

            {flagType === 'injury' && (
              <>
                {/* Summary chip */}
                <div className="hf-summary-chip">
                  <span>{getLocationLabel()}</span>
                  {injuryCategory && (
                    <span className="hf-summary-category">
                      {INJURY_CATEGORIES.find(c => c.id === injuryCategory)?.label}
                    </span>
                  )}
                </div>

                <div className="hf-detail-section">
                  <div className="tl-control-label" style={{ marginBottom: 10 }}>
                    Pain Scale — {painScale}/10
                  </div>
                  <div className="pain-scale">
                    {Array.from({ length: 10 }, (_, i) => i + 1).map(n => {
                      const hue = Math.round(120 - (n - 1) * 12);
                      const isActive = painScale === n;
                      return (
                        <button
                          key={n}
                          className={`pain-scale-btn${isActive ? ' pain-scale-btn--active' : ''}`}
                          style={isActive ? {
                            background: `hsl(${hue}, 75%, 45%)`,
                            borderColor: `hsl(${hue}, 75%, 45%)`,
                            color: '#fff',
                          } : {}}
                          onClick={() => setPainScale(n)}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>
                  <div className="pain-scale-labels">
                    <span>No pain</span>
                    <span>Worst imaginable</span>
                  </div>
                </div>
              </>
            )}

            <div className="hf-detail-section">
              <label className="tl-control-label">
                {flagType === 'injury'
                  ? 'What happened? When / where did it start?'
                  : 'How are you feeling? Any details?'}
              </label>
              <textarea
                className="tl-notes-textarea"
                style={{ marginTop: 8 }}
                placeholder={
                  flagType === 'injury'
                    ? 'e.g. Left knee pain started during tempo run, felt a pop around mile 3...'
                    : 'e.g. Woke up with a sore throat, low-grade fever since yesterday...'
                }
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={4}
                autoFocus
              />
            </div>

            {saveError && (
              <div style={{ padding: '0 24px 12px', color: '#dc2626', fontSize: 13 }}>{saveError}</div>
            )}

            <div className="tl-modal-footer">
              <button
                className="tl-btn-back"
                type="button"
                onClick={() => setScreen(flagType === 'injury' ? 'category' : 'illness')}
              >
                ← Back
              </button>
              <button
                className="btn btn-primary"
                style={{
                  width: 'auto',
                  padding: '10px 24px',
                  background: '#dc2626',
                  borderColor: '#dc2626',
                }}
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? 'Sending...' : 'Flag to Coach'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}